/**
 * Content Bundler Utility
 * Manages pre-packaged content records for offline/bundled deployment
 */

import type { TableData } from '../types/api/tables';
import { Base64ImageStorage } from '../storage/Base64ImageStorage';
import { ActionDefStorage } from '../storage/ActionDefStorage';
import { WellCheckStorage } from '../storage/WellCheckStorage';
import { ActivitiesDefStorage } from '../storage/ActivitiesDefStorage';
import { GuidanceDefStorage } from '../storage/GuidanceDefStorage';
import { SkinTypeDefStorage } from '../storage/SkinTypeDefStorage';
import { AdminAssessmentStorage } from '../services/AdminAssessmentStorage';
import type { AdminAssessmentDto } from '../types/adminAssessment';

export interface BundledContentRecord {
  id: string;
  contentType: string;
  languageId: string;
  payload: any;
  version: string;
  bundledAt: string;
}

export interface ContentBundle {
  version: string;
  generatedAt: string;
  records: BundledContentRecord[];
  metadata: {
    totalRecords: number;
    contentTypes: string[];
    languages: string[];
    description: string;
  };
}

/**
 * Configuration for required content that should be bundled with the app
 */
let REQUIRED_CONTENT_CONFIG = {
  // Essential AdminSegments that should always be available offline
  adminSegments: [
    { id: '1987', languageId: '1', description: 'PHQ-9 Assessment' },
    { id: '11255', languageId: '1', description: 'Standard Milestones (Communication, Motor, Cognitive, Social-Emotional)' },
    { id: '11270', languageId: '1', description: 'Premium Milestones (All domains with detailed items)' },
    // Add more critical AdminSegments here
  ],
  
  // Other content types that should be bundled
  otherContent: [
    { contentType: 'SystemConfig', id: 'app_settings', languageId: '1' },
    { contentType: 'LocalizationStrings', id: 'en_US', languageId: '1' },
    // Add more essential content here
  ],
  
  // Bundle metadata
  bundleInfo: {
    description: 'Essential content for offline functionality',
    version: '1.0.0'
  }
};

/**
 * Dynamic Bundle Configuration Manager
 */
export class BundleConfigManager {
  constructor(private storage: any) {}

  private parseContentTypeIdFromRecordId(recordId: string | undefined): string | null {
    if (!recordId) return null;
    const parts = recordId.split('_');
    if (parts.length >= 3) {
      return parts[1] || null;
    }
    return null;
  }

  /**
   * Get current bundle configuration
   */
  getBundleConfig() {
    return REQUIRED_CONTENT_CONFIG;
  }

  /**
   * Update bundle config from current Content Store entries
   */
  async updateConfigFromContentStore(useContentStore: any): Promise<void> {
    if (!useContentStore) {
      throw new Error('Content store not available');
    }

    const state = useContentStore.getState();
    const currentRecords = state?.getRecordsWithContentFields ? 
      state.getRecordsWithContentFields() : 
      (state?.records || []);

    // Reset and rebuild config from current content store
    const newAdminSegments = [];
    const newOtherContent = [];

    for (const record of currentRecords) {
      const contentFields = record.contentFields || null;
      const contentType = contentFields?.content_type || record.contentType || 'AdminSegment';
      const recordId =
        contentFields?.content_type_id
        || record.contentTypeId
        || this.parseContentTypeIdFromRecordId(record.id)
        || record.id
        || 'unknown';
      const languageId =
        contentFields?.content_languagetype_id
        || record.languageTypeId
        || record.languageId
        || '1';
      const description = contentFields?.content_title || record.name || record.title || `${contentType} ${recordId}`;

      if (contentType === 'AdminSegment') {
        newAdminSegments.push({
          id: recordId,
          languageId: languageId,
          description: description
        });
      } else {
        newOtherContent.push({
          contentType: contentType,
          id: recordId,
          languageId: languageId,
          description: description
        });
      }
    }

    // Update the global config
    REQUIRED_CONTENT_CONFIG = {
      ...REQUIRED_CONTENT_CONFIG,
      adminSegments: newAdminSegments,
      otherContent: newOtherContent
    };

    // Save to storage for persistence
    await this.saveBundleConfig();

    console.log('📋 Bundle config updated from Content Store:', {
      adminSegments: newAdminSegments.length,
      otherContent: newOtherContent.length
    });
  }

  /**
   * Add specific item to bundle config
   */
  async addToBundleConfig(item: {
    id: string;
    contentType?: string;
    languageId?: string;
    description?: string;
  }): Promise<void> {
    const contentType = item.contentType || 'AdminSegment';
    const languageId = item.languageId || '1';
    const description = item.description || `${contentType} ${item.id}`;

    if (contentType === 'AdminSegment') {
      // Check if already exists
      const exists = REQUIRED_CONTENT_CONFIG.adminSegments.some(
        seg => seg.id === item.id && seg.languageId === languageId
      );
      
      if (!exists) {
        REQUIRED_CONTENT_CONFIG.adminSegments.push({
          id: item.id,
          languageId: languageId,
          description: description
        });
      }
    } else {
      // Check if already exists
      const exists = REQUIRED_CONTENT_CONFIG.otherContent.some(
        content => content.id === item.id && 
                  content.contentType === contentType &&
                  content.languageId === languageId
      );
      
      if (!exists) {
        REQUIRED_CONTENT_CONFIG.otherContent.push({
          contentType: contentType,
          id: item.id,
          languageId: languageId
        });
      }
    }

    await this.saveBundleConfig();
    console.log(`📋 Added ${contentType} ${item.id} to bundle config`);
  }

  /**
   * Remove item from bundle config
   */
  async removeFromBundleConfig(id: string, contentType?: string): Promise<void> {
    if (!contentType || contentType === 'AdminSegment') {
      REQUIRED_CONTENT_CONFIG.adminSegments = REQUIRED_CONTENT_CONFIG.adminSegments.filter(
        seg => seg.id !== id
      );
    } else {
      REQUIRED_CONTENT_CONFIG.otherContent = REQUIRED_CONTENT_CONFIG.otherContent.filter(
        content => !(content.id === id && content.contentType === contentType)
      );
    }

    await this.saveBundleConfig();
    console.log(`📋 Removed ${contentType || 'AdminSegment'} ${id} from bundle config`);
  }

  /**
   * Reset bundle config to default
   */
  async resetBundleConfig(): Promise<void> {
    REQUIRED_CONTENT_CONFIG = {
      adminSegments: [
        { id: '1987', languageId: '1', description: 'PHQ-9 Assessment' },
        { id: '11255', languageId: '1', description: 'Standard Milestones' },
        { id: '11270', languageId: '1', description: 'Premium Milestones' },
      ],
      otherContent: [
        { contentType: 'SystemConfig', id: 'app_settings', languageId: '1' },
        { contentType: 'LocalizationStrings', id: 'en_US', languageId: '1' },
      ],
      bundleInfo: {
        description: 'Essential content for offline functionality',
        version: '1.0.0'
      }
    };

    await this.saveBundleConfig();
    console.log('📋 Bundle config reset to default');
  }

  /**
   * Save bundle config to storage
   */
  private async saveBundleConfig(): Promise<void> {
    if (!this.storage) return;
    
    try {
      await this.storage.setItem('bundle_config', JSON.stringify(REQUIRED_CONTENT_CONFIG));
    } catch (error) {
      console.error('Failed to save bundle config:', error);
    }
  }

  /**
   * Load bundle config from storage
   */
  async loadBundleConfig(): Promise<void> {
    if (!this.storage) return;
    
    try {
      const savedConfig = await this.storage.getItem('bundle_config');
      if (savedConfig) {
        REQUIRED_CONTENT_CONFIG = JSON.parse(savedConfig);
        console.log('📋 Bundle config loaded from storage');
      }
    } catch (error) {
      console.error('Failed to load bundle config:', error);
    }
  }

  /**
   * Get detailed bundle config info
   */
  getBundleConfigDetails() {
    return {
      adminSegments: REQUIRED_CONTENT_CONFIG.adminSegments.map(seg => `${seg.id} - ${seg.description}`),
      otherContent: REQUIRED_CONTENT_CONFIG.otherContent.map(content => `${content.contentType}:${content.id}`),
      totalItems: REQUIRED_CONTENT_CONFIG.adminSegments.length + REQUIRED_CONTENT_CONFIG.otherContent.length,
      version: REQUIRED_CONTENT_CONFIG.bundleInfo.version
    };
  }
}

// Export the current config for backward compatibility
export { REQUIRED_CONTENT_CONFIG };

/**
 * Development utility to generate content bundle from current DebugView downloads
 */
export class ContentBundleGenerator {
  private bundleConfigManager: BundleConfigManager;

  constructor(
    private storage: any,
    private useContentStore: any,
    private contentService?: any
  ) {
    this.bundleConfigManager = new BundleConfigManager(storage);
  }

  /**
   * Get the bundle config manager
   */
  getBundleConfigManager(): BundleConfigManager {
    return this.bundleConfigManager;
  }

  /**
   * Generate a content bundle from currently downloaded content
   */
  async generateBundle(): Promise<ContentBundle> {
    console.log('📦 ContentBundleGenerator: Starting bundle generation...');
    
    const records: BundledContentRecord[] = [];
    const contentTypes = new Set<string>();
    const languages = new Set<string>();

    // Get all current content from store
    if (this.useContentStore) {
      const state = this.useContentStore.getState();
      const currentRecords = state?.getRecordsWithContentFields ? 
        state.getRecordsWithContentFields() : 
        (state?.records || []);

      console.log(`📦 Found ${currentRecords.length} records in content store`);

      for (const record of currentRecords) {
        console.log(`📦 Processing record:`, {
          id: record.id || record.contentId,
          type: record.contentType,
          hasPayload: !!(record.payload || record.content || record.data),
          payloadType: typeof (record.payload || record.content || record.data),
          recordKeys: Object.keys(record)
        });

        // Extract the actual content data - try multiple possible locations
        let actualPayload = null;
        
        // Try different payload locations
        if (record.payload) {
          actualPayload = record.payload;
        } else if (record.content) {
          actualPayload = record.content;
        } else if (record.data) {
          actualPayload = record.data;
        } else if (record.tableData) {
          // If it's stored as TableData structure, use that
          actualPayload = record.tableData;
        } else if (record.raw) {
          // If it's raw data from API
          actualPayload = record.raw;
        } else {
          // Last resort - include the entire record structure
          console.warn(`📦 No payload found for record ${record.id}, using entire record`);
          actualPayload = { ...record };
        }

        // Ensure we have actual content, not just metadata
        if (!actualPayload || (typeof actualPayload === 'object' && Object.keys(actualPayload).length === 0)) {
          console.warn(`📦 Empty or null payload for record ${record.id}, skipping`);
          continue;
        }

        console.log(`📦 Including payload for ${record.id}:`, {
          payloadSize: JSON.stringify(actualPayload).length,
          payloadKeys: typeof actualPayload === 'object' ? Object.keys(actualPayload) : 'not object'
        });

        // Extract essential info for bundling
        const bundledRecord: BundledContentRecord = {
          id: record.id || record.contentId || 'unknown',
          contentType: record.contentType || 'AdminSegment',
          languageId: record.languageTypeId || record.languageId || '1',
          payload: actualPayload, // Use the extracted actual content
          version: record.syncVersion || record.version || '1.0',
          bundledAt: new Date().toISOString()
        };

        records.push(bundledRecord);
        contentTypes.add(bundledRecord.contentType);
        languages.add(bundledRecord.languageId);
      }
    }

    const bundle: ContentBundle = {
      version: REQUIRED_CONTENT_CONFIG.bundleInfo.version,
      generatedAt: new Date().toISOString(),
      records,
      metadata: {
        totalRecords: records.length,
        contentTypes: Array.from(contentTypes),
        languages: Array.from(languages),
        description: REQUIRED_CONTENT_CONFIG.bundleInfo.description
      }
    };

    console.log('📦 Generated content bundle:', {
      totalRecords: bundle.metadata.totalRecords,
      contentTypes: bundle.metadata.contentTypes,
      languages: bundle.metadata.languages
    });

    return bundle;
  }

  /**
   * Save bundle to storage for development/testing
   */
  async saveBundleToStorage(bundle: ContentBundle): Promise<void> {
    if (!this.storage) {
      throw new Error('Storage not available');
    }

    try {
      await this.storage.setItem('content_bundle', JSON.stringify(bundle));
      console.log('📦 Content bundle saved to storage');
    } catch (error) {
      console.error('📦 Failed to save content bundle:', error);
      throw error;
    }
  }

  /**
   * Export bundle as JSON string for file deployment
   */
  exportBundleAsJSON(bundle: ContentBundle): string {
    return JSON.stringify(bundle, null, 2);
  }

  /**
   * Download specific required content for bundling
   */
  async downloadRequiredContent(): Promise<void> {
    if (!this.contentService) {
      throw new Error('Content sync service not available');
    }

    console.log('📦 Downloading required content for bundling...');
    
    // Download all required AdminSegments
    for (const segment of REQUIRED_CONTENT_CONFIG.adminSegments) {
      try {
        console.log(`📦 Downloading AdminSegment ${segment.id} (${segment.description})`);
        
        const query = {
          ContentType: 'AdminSegment',
          ContentId: segment.id,
          LanguageId: segment.languageId
        };

        await this.contentService.downloadContent(query);
        console.log(`✅ Downloaded AdminSegment ${segment.id}`);
      } catch (error) {
        console.error(`❌ Failed to download AdminSegment ${segment.id}:`, error);
      }
    }

    // Download other required content
    for (const content of REQUIRED_CONTENT_CONFIG.otherContent) {
      try {
        console.log(`📦 Downloading ${content.contentType} ${content.id}`);
        
        const query = {
          ContentType: content.contentType,
          ContentId: content.id,
          LanguageId: content.languageId
        };

        await this.contentService.downloadContent(query);
        console.log(`✅ Downloaded ${content.contentType} ${content.id}`);
      } catch (error) {
        console.error(`❌ Failed to download ${content.contentType} ${content.id}:`, error);
      }
    }

    console.log('📦 Required content download complete');
  }
}

/**
 * Content Bundle Loader - loads pre-packaged content into the app
 */
export class ContentBundleLoader {
  constructor(
    protected storage: any,
    protected useContentStore: any
  ) {}

  /**
   * Load bundled content into the content store
   */
  async loadBundledContent(bundle: ContentBundle): Promise<void> {
    console.log('📥 ContentBundleLoader: Loading bundled content...');
    console.log(`📥 Bundle version: ${bundle.version}, Records: ${bundle.metadata.totalRecords}`);

    if (!this.useContentStore) {
      throw new Error('Content store not available');
    }

    const state = this.useContentStore.getState();
    let loadedCount = 0;

    // Diagnostic: log all content types present in the bundle
    const typeSummary = bundle.records.reduce((acc: Record<string, number>, r) => {
      acc[r.contentType] = (acc[r.contentType] ?? 0) + 1;
      return acc;
    }, {});
    console.log(`📥 ContentBundleLoader: bundle contains ${bundle.records.length} record(s):`, typeSummary);

    for (const record of bundle.records) {
      try {
        console.log(`📥 Loading bundled record: ${record.contentType} ${record.id}`, {
          hasPayload: !!record.payload,
          payloadType: typeof record.payload,
          payloadSize: record.payload ? JSON.stringify(record.payload).length : 0
        });

        // Validate that we have actual content
        if (!record.payload || (typeof record.payload === 'object' && Object.keys(record.payload).length === 0)) {
          console.warn(`📥 Skipping record ${record.id} - no payload data`);
          continue;
        }

        // Extract ContentId from record.id (e.g., "AdminSegment_11255_1" -> "11255")
        const contentIdFromRecord = record.id.includes('_') ? 
          record.id.split('_')[1] : // Extract ID from "AdminSegment_11255_1" -> "11255"
          record.id.replace(`${record.contentType}_`, '').split('_')[0]; // Alternative extraction

        // Create ContentQuery object with proper field names that match ContentStore interface
        const contentQuery = {
          contentType: record.contentType,           // lowercase 'contentType'
          contentTypeId: contentIdFromRecord,        // 'contentTypeId' (not 'ContentId')
          languageTypeId: record.languageId         // 'languageTypeId' (not 'LanguageId')
        };

        console.log(`📥 Content query extracted:`, {
          from: record.id,
          ContentType: record.contentType,
          ContentId: contentIdFromRecord,
          LanguageId: record.languageId,
          query: contentQuery
        });

        // Convert payload back to TableData format if it's not already
        let tableData;
        if (record.payload.Schema && record.payload.Data) {
          // Already in TableData format
          tableData = record.payload;
        } else {
          // Wrap the payload in a basic TableData structure
          tableData = {
            Schema: {
              TableName: record.contentType,
              Fields: []
            },
            Data: {
              Rows: Array.isArray(record.payload) ? record.payload : [record.payload]
            }
          };
        }

        console.log(`📥 Restoring to ContentStore: ${contentQuery.contentType} ${contentQuery.contentTypeId}`, {
          tableDataValid: !!(tableData.Schema && tableData.Data),
          rowCount: tableData.Data?.Rows?.length || 0
        });

        // Add to content store using the proper API
        if (state?.addOrUpdateContent) {
          // Use the proper ContentStore API
          state.addOrUpdateContent(contentQuery, tableData);
        } else {
          console.warn(`📥 ContentStore addOrUpdateContent method not available for ${record.id}`);
          continue;
        }

        // === Specialized unpack for AdminAssessment records ===
        // Extract each row's content_body (base64-encoded AdminAssessmentDto) and
        // persist it into AdminAssessmentStorage so WellVisitView can list/start assessments.
        if (record.contentType === 'AdminAssessment' && this.storage) {
          try {
            const columns: string[] = tableData.Data?.Columns ?? [];
            const rows: any[][] = tableData.Data?.Rows ?? [];
            const bodyIdx = columns.findIndex((c: string) => c.toLowerCase() === 'content_body');

            if (bodyIdx === -1 || rows.length === 0) {
              console.warn(`📥 ⚠️ AdminAssessment bundle record ${record.id}: no content_body column or rows`);
            } else {
              const adminAssessmentStorage = new AdminAssessmentStorage(this.storage);

              for (const row of rows) {
                const base64Content = row[bodyIdx] as string;
                if (!base64Content) continue;

                // Decode base64 → JSON string (Node.js Buffer or browser atob)
                const jsonString: string = typeof Buffer !== 'undefined'
                  ? Buffer.from(base64Content, 'base64').toString('utf-8')
                  : atob(base64Content);

                const parsed = JSON.parse(jsonString);

                // Normalise PascalCase (server) → camelCase (TS interface)
                const dto: AdminAssessmentDto = {
                  dName:          parsed.DName          ?? parsed.dName          ?? '',
                  sourceId:       parsed.SourceId       ?? parsed.sourceId       ?? '',
                  surveyId:       parsed.SurveyId       ?? parsed.surveyId       ?? '',
                  languageTypeId: parsed.LanguageTypeId ?? parsed.languageTypeId ?? '1',
                  surveybList:    parsed.SurveybList    ?? parsed.surveybList    ?? '',
                  extra:          parsed.Extra          ?? parsed.extra          ?? '',
                  reports:        parsed.Reports        ?? parsed.reports        ?? [],
                  aggVars:        parsed.AggVars        ?? parsed.aggVars        ?? [],
                };

                await adminAssessmentStorage.addSingleAssessment(dto);
                console.log(`📥 ✅ AdminAssessmentDto extracted from bundle: ${dto.dName} (sourceId: ${dto.sourceId})`);
              }
            }
          } catch (assessmentErr) {
            console.error(`📥 ❌ AdminAssessment bundle extraction failed for ${record.id}:`, assessmentErr);
          }
        }

        // === Specialized unpack for Base64Image records ===
        if (record.contentType === 'Base64Image' && this.storage) {
          try {
            console.log(`📥 Unpacking Base64Image bundle record: ${record.id}`);
            const imageStorage = new Base64ImageStorage(this.storage);
            const syntheticContentRecord = {
              id: record.id,
              contentType: record.contentType,
              tableData,
            } as any;
            const dto = await imageStorage.unpackFromContentRecord(syntheticContentRecord);
            if (dto) {
              console.log(`📥 ✅ Base64Image unpacked from bundle: ${dto.imageId} (${dto.dName})`);
            } else {
              console.warn(`📥 ⚠️ Base64Image bundle unpack returned null for ${record.id}`);
            }
          } catch (imageErr) {
            console.error(`📥 ❌ Base64Image bundle unpack failed for ${record.id}:`, imageErr);
          }
        }

        // === Specialized unpack for ActionDef records ===
        if (record.contentType === 'ActionDef' && this.storage) {
          try {
            console.log(`📥 Unpacking ActionDef bundle record: ${record.id}`);
            const actionDefStorage = new ActionDefStorage(this.storage);
            const syntheticContentRecord = {
              id: record.id,
              contentType: record.contentType,
              tableData,
            } as any;
            const dto = await actionDefStorage.unpackFromContentRecord(syntheticContentRecord);
            if (dto) {
              console.log(`📥 ✅ ActionDef unpacked from bundle: source="${dto.source}" lang="${dto.languageTypeId}" (${dto.actionGroups.length} groups, ${dto.actionTypes.length} types)`);
            } else {
              console.warn(`📥 ⚠️ ActionDef bundle unpack returned null for ${record.id}`);
            }
          } catch (actionDefErr) {
            console.error(`📥 ❌ ActionDef bundle unpack failed for ${record.id}:`, actionDefErr);
          }
        }

        // === Specialized unpack for WellCheckDef records ===
        if (record.contentType === 'WellCheckDef' && this.storage) {
          try {
            console.log(`📥 Unpacking WellCheckDef bundle record: ${record.id}`);
            const wellCheckStorage = new WellCheckStorage(this.storage);
            const syntheticContentRecord = {
              id: record.id,
              contentType: record.contentType,
              tableData,
            } as any;
            const dto = await wellCheckStorage.unpackFromContentRecord(syntheticContentRecord);
            if (dto) {
              console.log(`📥 ✅ WellCheckDef unpacked from bundle: lang="${dto.languageTypeId}" (${dto.schedules.length} schedule(s), ${dto.vaccines.length} vaccine(s))`);
            } else {
              console.warn(`📥 ⚠️ WellCheckDef bundle unpack returned null for ${record.id}`);
            }
          } catch (wellCheckErr) {
            console.error(`📥 ❌ WellCheckDef bundle unpack failed for ${record.id}:`, wellCheckErr);
          }
        }

        // === Specialized unpack for ActivitiesDef records ===
        // Note: content type on the wire is 'ActivityDef' (singular), matching the backend.
        if (record.contentType === 'ActivityDef' && this.storage) {
          try {
            console.log(`📥 Unpacking ActivityDef bundle record: ${record.id}`);
            const activitiesDefStorage = new ActivitiesDefStorage(this.storage);
            const syntheticContentRecord = {
              id: record.id,
              contentType: record.contentType,
              tableData,
            } as any;
            const dto = await activitiesDefStorage.unpackFromContentRecord(syntheticContentRecord);
            if (dto) {
              console.log(`📥 ✅ ActivityDef unpacked from bundle: source="${dto.sourceId}" lang="${dto.languageTypeId}" (${dto.domains.length} domain(s))`);
            } else {
              console.warn(`📥 ⚠️ ActivityDef bundle unpack returned null for ${record.id}`);
            }
          } catch (activitiesDefErr) {
            console.error(`📥 ❌ ActivityDef bundle unpack failed for ${record.id}:`, activitiesDefErr);
          }
        }

        // === Specialized unpack for GuidanceDef records ===
        if (record.contentType === 'GuidanceDef' && this.storage) {
          try {
            console.log(`📥 Unpacking GuidanceDef bundle record: ${record.id}`);
            const guidanceDefStorage = new GuidanceDefStorage(this.storage);
            const syntheticContentRecord = {
              id: record.id,
              contentType: record.contentType,
              tableData,
            } as any;
            const dto = await guidanceDefStorage.unpackFromContentRecord(syntheticContentRecord);
            if (dto) {
              console.log(`📥 ✅ GuidanceDef unpacked from bundle: source="${dto.sourceId}" lang="${dto.languageTypeId}" (${dto.ageGroups.length} age group(s))`);
            } else {
              console.warn(`📥 ⚠️ GuidanceDef bundle unpack returned null for ${record.id}`);
            }
          } catch (guidanceDefErr) {
            console.error(`📥 ❌ GuidanceDef bundle unpack failed for ${record.id}:`, guidanceDefErr);
          }
        }

        // === Specialized unpack for skintypedef records ===
        if ((record.contentType || '').toLowerCase() === 'skintypedef' && this.storage) {
          try {
            console.log(`📥 Unpacking skintypedef bundle record: ${record.id}`);
            const skinTypeDefStorage = new SkinTypeDefStorage(this.storage);
            const syntheticContentRecord = {
              id: record.id,
              contentType: record.contentType,
              tableData,
            } as any;
            const dto = await skinTypeDefStorage.unpackFromContentRecord(syntheticContentRecord);
            if (dto) {
              console.log(`📥 ✅ skintypedef unpacked from bundle: skinType="${dto.skinType}" lang="${dto.languageTypeId}"`);
            } else {
              console.warn(`📥 ⚠️ skintypedef bundle unpack returned null for ${record.id}`);
            }
          } catch (skinTypeDefErr) {
            console.error(`📥 ❌ skintypedef bundle unpack failed for ${record.id}:`, skinTypeDefErr);
          }
        }

        loadedCount++;
        console.log(`📥 ✅ Successfully loaded ${record.contentType} ${record.id}`);
      } catch (error) {
        console.error(`📥 ❌ Failed to load ${record.contentType} ${record.id}:`, error);
      }
    }

    console.log(`📥 Loaded ${loadedCount}/${bundle.records.length} bundled records`);
  }

  /**
   * Check if bundled content needs to be loaded
   */
  async shouldLoadBundledContent(): Promise<boolean> {
    if (!this.storage) return true;

    try {
      const lastLoaded = await this.storage.getItem('bundled_content_loaded');
      const bundleVersion = await this.storage.getItem('bundled_content_version');
      
      // Load if never loaded or version mismatch
      if (!lastLoaded || bundleVersion !== REQUIRED_CONTENT_CONFIG.bundleInfo.version) {
        return true;
      }

      // Load if it's been more than 30 days (optional refresh)
      const lastLoadedDate = new Date(lastLoaded);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      return lastLoadedDate < thirtyDaysAgo;
    } catch (error) {
      console.error('📥 Error checking bundled content status:', error);
      return true;
    }
  }

  /**
   * Mark bundled content as loaded
   */
  async markBundledContentLoaded(version: string): Promise<void> {
    if (!this.storage) return;

    try {
      await this.storage.setItem('bundled_content_loaded', new Date().toISOString());
      await this.storage.setItem('bundled_content_version', version);
      console.log('📥 Marked bundled content as loaded');
    } catch (error) {
      console.error('📥 Failed to mark bundled content as loaded:', error);
    }
  }
}